import React, { useState, useEffect } from 'react';
import { Save, XCircle, Loader } from "lucide-react";
import Select from "react-select";

// Import shared types
import { FirmFormProps, FirmFormData } from '../../components/common/types';
import { getAllHeadquarters, getAllLocations, getAllZones } from '../../api/master';
import { useAuth } from '../../context/AuthContext';
import { getAllEmployees } from '../../api/employee';
import EmployeeMain from '../../types/employee';

type SelectOption = {
  value: number;
  label: string;
};


interface Zone {
  id: number;
  name: string;
}

interface HeadQuarter {
  id: number;
  name: string;
}

interface Location {
  id: number;
  name: string;
  head_quarter_id: number;
  latitude: string;
  longitude: string;
}


const FirmForm: React.FC<FirmFormProps> = ({ firm, onSubmit, onCancel }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState<FirmFormData>({
    tenant_id: user.tenant_id,
    zone_id: 0,
    head_quarter_id: 0,
    location_id: 0,
    employee_id: 0,
    name: "",
    type: "chemist",
    contact_person: "",
    contact_no: "",
    email: "",
    city: "",
    address: "",
    latitude: "",
    longitude: "",
  });

  const userChangedHq = React.useRef(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [zones, setZones] = useState<Zone[]>([]);
  const [headQuarters, setHeadQuarters] = useState<HeadQuarter[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [employees, setEmployees] = useState<EmployeeMain[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<Location[]>([]);

  const [coordinatesManuallyChanged, setCoordinatesManuallyChanged] = useState({
    latitude: false,
    longitude: false
  });


  useEffect(() => {
    if (!formData.head_quarter_id) {
      setFilteredLocations([]);
      return;
    }

    const filtered = locations.filter(
      loc => loc.head_quarter_id === formData.head_quarter_id
    );

    setFilteredLocations(filtered);

    // Only reset dependent fields when the user explicitly changed the HQ
    if (!userChangedHq.current) return;
    userChangedHq.current = false;

    setFormData(prev => ({
      ...prev,
      city: "",
      location_id: 0,
      latitude: "",
      longitude: ""
    }));

    setCoordinatesManuallyChanged({
      latitude: false,
      longitude: false
    });

  }, [formData.head_quarter_id, locations]);


  // Fetch zone data
  const fetchZones = async () => {
    try {
      const response = await getAllZones(user.tenant_id);
      setZones(response);
    } catch (error) {
      console.error('Error fetching zones:', error);
    }
  };

  // Fetch headquarters data
  const fetchHeadQuarters = async () => {
    try {
      const response = await getAllHeadquarters(user.tenant_id);
      setHeadQuarters(response);
    } catch (error) {
      console.error('Error fetching headquarters:', error);
    }
  };

  // Fetch locations data
  const fetchLocations = async () => {
    try {
      const response = await getAllLocations(user.tenant_id);
      setLocations(response);
    } catch (error) {
      console.error('Error fetching locations:', error);
    }
  };

  // Fetch employees data
  const fetchEmployees = async () => {
    try {
      const response = await getAllEmployees(user.tenant_id);
      setEmployees(response);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Load all API data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchZones(),
        fetchHeadQuarters(),
        fetchLocations(),
        fetchEmployees(),
      ]);
      setLoading(false);
    };

    loadData();
  }, []);

  // Initialize form with doctor data if editing
  useEffect(() => {
    if (firm) {
      setFormData({
        tenant_id: user.tenant_id,
        zone_id: firm.zone_id,
        head_quarter_id: firm.head_quarter_id,
        location_id: firm.location_id,
        employee_id: firm.employee_id,
        name: firm.name,
        type: firm.type,
        contact_person: firm.contact_person || "",
        contact_no: firm.contact_no || "",
        email: firm.email || "",
        city: firm.city || "",
        address: firm.address || "",
        latitude: firm.latitude || "",
        longitude: firm.longitude || "",
      });

      // If firm has existing coordinates, mark them as manually set
      if (firm.latitude || firm.longitude) {
        setCoordinatesManuallyChanged({
          latitude: !!firm.latitude,
          longitude: !!firm.longitude
        });
      }
    } else {
      // For new doctors, set tenant_id from user
      setFormData(prev => ({
        ...prev,
        tenant_id: user.tenant_id
      }));
    }
  }, [firm, user.tenant_id]);

  useEffect(() => {
    if (!formData.location_id || filteredLocations.length === 0) return;

    const selectedLocation = filteredLocations.find(
      loc => loc.id === formData.location_id
    );

    if (!selectedLocation) return;

    setFormData(prev => ({
      ...prev,
      city: selectedLocation.name,
      latitude: !coordinatesManuallyChanged.latitude
        ? selectedLocation.latitude
        : prev.latitude,
      longitude: !coordinatesManuallyChanged.longitude
        ? selectedLocation.longitude
        : prev.longitude
    }));
  }, [formData.location_id, filteredLocations]);

  // Function to handle city selection
  // const handleCityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
  //   const selectedCity = e.target.value;

  //   // Find the location data for the selected city
  //   const selectedLocation = locations.find(loc => loc.name === selectedCity);

  //   // Clear error for this field
  //   if (errors.city) {
  //     setErrors(prev => {
  //       const newErrors = { ...prev };
  //       delete newErrors.city;
  //       return newErrors;
  //     });
  //   }

  //   // Update form data with city name
  //   setFormData(prev => ({
  //     ...prev,
  //     city: selectedCity,
  //     // Only auto-fill coordinates if they haven't been manually changed
  //     latitude: !coordinatesManuallyChanged.latitude && selectedLocation ? selectedLocation.latitude : prev.latitude,
  //     longitude: !coordinatesManuallyChanged.longitude && selectedLocation ? selectedLocation.longitude : prev.longitude
  //   }));

  //   // If city was selected from dropdown (not manually typed), also update location_id
  //   if (selectedLocation) {
  //     setFormData(prev => ({
  //       ...prev,
  //       location_id: selectedLocation.id
  //     }));
  //   }
  // };

  // Function to handle manual city input
  // const handleManualCityInput = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const cityValue = e.target.value;

  //   // Clear error for this field
  //   if (errors.city) {
  //     setErrors(prev => {
  //       const newErrors = { ...prev };
  //       delete newErrors.city;
  //       return newErrors;
  //     });
  //   }

  //   // Try to find if this matches any location
  //   const matchingLocation = locations.find(loc =>
  //     loc.name.toLowerCase().includes(cityValue.toLowerCase())
  //   );

  //   setFormData(prev => ({
  //     ...prev,
  //     city: cityValue,
  //     // If matching location found and coordinates not manually changed, update them
  //     latitude: !coordinatesManuallyChanged.latitude && matchingLocation ? matchingLocation.latitude : prev.latitude,
  //     longitude: !coordinatesManuallyChanged.longitude && matchingLocation ? matchingLocation.longitude : prev.longitude,
  //     // If matching location found, set location_id
  //     location_id: matchingLocation ? matchingLocation.id : prev.location_id
  //   }));
  // };

  // Function to handle coordinate changes (mark them as manually changed)
  const handleCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Mark this coordinate as manually changed
    if (name === 'latitude' && value.trim() !== '') {
      setCoordinatesManuallyChanged(prev => ({ ...prev, latitude: true }));
    }
    if (name === 'longitude' && value.trim() !== '') {
      setCoordinatesManuallyChanged(prev => ({ ...prev, longitude: true }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required fields validation
    if (!formData.tenant_id) newErrors.tenant_id = "Tenant ID is required";
    if (!formData.zone_id) newErrors.zone_id = "Zone is required";
    if (!formData.head_quarter_id) newErrors.head_quarter_id = "Head Quarter is required";
    if (!formData.location_id || formData.location_id === 0) {
      newErrors.location_id = "Location is required";
    }
    if (!formData.employee_id) newErrors.employee_id = "Employee is required";
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.type) newErrors.type = "Type is required";
    if (!formData.contact_person) newErrors.contact_person = "Contact Person is required";
    if (!formData.contact_no) newErrors.contact_no = "Contact No is required";
    if (!formData.address) newErrors.address = "Address is required";
    if (!formData.latitude) newErrors.latitude = "Latitude is required";
    if (!formData.longitude) newErrors.longitude = "Longitude is required";

    // Coordinate validation
    if (formData.latitude && (Number(formData.latitude) < -90 || Number(formData.latitude) > 90)) {
      newErrors.latitude = "Latitude must be between -90 and 90";
    }
    if (formData.longitude && (Number(formData.longitude) < -180 || Number(formData.longitude) > 180)) {
      newErrors.longitude = "Longitude must be between -180 and 180";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const zoneOptions: SelectOption[] = zones.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const headQuaterOptions: SelectOption[] = headQuarters.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const locationOptions: SelectOption[] = filteredLocations.map((item) => ({
    value: item.id,
    label: item.name,
  }));

  const employeeOptions: SelectOption[] = employees.map((item) => ({
    value: item.id,
    label: item.user.name,
  }));

  const typeOptions = [
    { value: "chemist", label: "Chemist" },
    { value: "stockist", label: "Stockist" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading form data...</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Zone and HeadQuarter */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="zone_id" className="block text-sm font-medium text-gray-700">
            Zone <span className="text-red-500">*</span>
          </label>

          <Select
            name="zone_id"
            options={zoneOptions}
            onChange={(selectedOption) => {
              setFormData(prev => ({
                ...prev,
                zone_id: selectedOption ? selectedOption.value : 0,
              }));
            }}
            value={zoneOptions.find(option => option.value === Number(formData.zone_id)) || null}
            className={`w-full ${errors.zone_id ? 'border-red-500' : 'border-gray-300'}`}
            isSearchable
            placeholder="Select Zone"
          />

          {errors.zone_id && (
            <p className="text-sm text-red-600">{errors.zone_id}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="head_quarter_id" className="block text-sm font-medium text-gray-700">
            Head Quarter <span className="text-red-500">*</span>
          </label>

          <Select
            name="head_quarter_id"
            options={headQuaterOptions}
            onChange={(selectedOption) => {
              userChangedHq.current = true;
              setFormData(prev => ({
                ...prev,
                head_quarter_id: selectedOption ? selectedOption.value : 0,
              }));
            }}
            value={headQuaterOptions.find(option => option.value === Number(formData.head_quarter_id)) || null}
            className={`w-full ${errors.head_quarter_id ? 'border-red-500' : 'border-gray-300'}`}
            isSearchable
            placeholder="Select Head Quarter"
          />

          {errors.head_quarter_id && (
            <p className="text-sm text-red-600">{errors.head_quarter_id}</p>
          )}
        </div>
      </div>

      {/* Location & Employee */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="location_id" className="block text-sm font-medium text-gray-700">
            Location <span className="text-red-500">*</span>
          </label>

          <Select
            name="location_id"
            options={locationOptions}
            onChange={(selectedOption) => {
              setFormData(prev => ({
                ...prev,
                location_id: selectedOption ? selectedOption.value : 0,
              }));
            }}
            value={locationOptions.find(option => option.value === Number(formData.location_id)) || null}
            isDisabled={!formData.head_quarter_id}
            className={`w-full ${errors.location_id ? 'border-red-500' : 'border-gray-300'}`}
            isSearchable
            placeholder={
              formData.head_quarter_id
                ? "Select City"
                : "Select Head Quarter first"
            }
          />

          {errors.location_id && (
            <p className="text-sm text-red-600">{errors.location_id}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="employee_id" className="block text-sm font-medium text-gray-700">
            Employee <span className="text-red-500">*</span>
          </label>

          <Select
            name="employee_id"
            options={employeeOptions}
            onChange={(selectedOption) => {
              setFormData(prev => ({
                ...prev,
                employee_id: selectedOption ? selectedOption.value : 0,
              }));
            }}
            value={employeeOptions.find(option => option.value === Number(formData.employee_id)) || null}
            className={`w-full ${errors.employee_id ? 'border-red-500' : 'border-gray-300'}`}
            isSearchable
            placeholder="Select Employee"
          />

          {errors.employee_id && (
            <p className="text-sm text-red-600">{errors.employee_id}</p>
          )}
        </div>
      </div>

      {/* Firm Name and Type */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Firm Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.name ? 'border-red-500' : 'border-gray-300'
              }`}
            required
            placeholder="Full Name"
          />
          {errors.name && (
            <p className="text-sm text-red-600">{errors.name}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="type" className="block text-sm font-medium text-gray-700">
            Type <span className="text-red-500">*</span>
          </label>

          <Select
            name="type"
            options={typeOptions}
            onChange={(selectedOption) => {
              setFormData(prev => ({
                ...prev,
                type: selectedOption ? selectedOption.value : "",
              }));
            }}
            value={typeOptions.find(option => option.value === formData.type) || null}
            className={`w-full ${errors.type ? 'border-red-500' : 'border-gray-300'}`}
            isSearchable
            placeholder="Select Type"
          />

          {errors.type && (
            <p className="text-sm text-red-600">{errors.type}</p>
          )}
        </div>
      </div>

      {/* Contact Person and Contact No. */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700">
            Contact Person <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contact_person"
            name="contact_person"
            value={formData.contact_person}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.contact_person ? 'border-red-500' : 'border-gray-300'
              }`}
            required
            placeholder="Contact Person Name"
          />
          {errors.contact_person && (
            <p className="text-sm text-red-600">{errors.contact_person}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="contact_no" className="block text-sm font-medium text-gray-700">
            Contact No. <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="contact_no"
            name="contact_no"
            value={formData.contact_no}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.contact_no ? 'border-red-500' : 'border-gray-300'
              }`}
            required
            placeholder="Contact Number"
          />
          {errors.contact_no && (
            <p className="text-sm text-red-600">{errors.contact_no}</p>
          )}
        </div>
      </div>

      {/* Email and City */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="Email"
          />
          {errors.email && (
            <p className="text-sm text-red-600">{errors.email}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="city" className="block text-sm font-medium text-gray-700">
            City
          </label>
          <input
            type="text"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.city ? 'border-red-500' : 'border-gray-300'
              }`}
            placeholder="City"
          />
          {errors.city && (
            <p className="text-sm text-red-600">{errors.city}</p>
          )}
        </div>
      </div>


      {/* Address */}
      <div className="space-y-2">
        <label htmlFor="address" className="block text-sm font-medium text-gray-700">
          Address <span className="text-red-500">*</span>
        </label>
        <textarea
          id="address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
          placeholder="Full address"
        />
        {errors.address && (
          <p className="text-sm text-red-600">{errors.address}</p>
        )}
      </div>

      {/* Auto-filled Coordinates with Info */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label htmlFor="latitude" className="block text-sm font-medium text-gray-700">
            Latitude <span className="text-red-500">*</span> {coordinatesManuallyChanged.latitude && <span className="text-green-600 text-xs">(Manually set)</span>}
          </label>
          <div className="relative">
            <input
              type="text"
              id="latitude"
              name="latitude"
              value={formData.latitude}
              onChange={handleCoordinateChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.latitude ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., 40.7128"
            />
            {formData.latitude && !coordinatesManuallyChanged.latitude && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  Auto-filled
                </span>
              </div>
            )}
          </div>
          {errors.latitude && (
            <p className="text-sm text-red-600">{errors.latitude}</p>
          )}
        </div>

        <div className="space-y-2">
          <label htmlFor="longitude" className="block text-sm font-medium text-gray-700">
            Longitude <span className="text-red-500">*</span> {coordinatesManuallyChanged.longitude && <span className="text-green-600 text-xs">(Manually set)</span>}
          </label>
          <div className="relative">
            <input
              type="text"
              id="longitude"
              name="longitude"
              value={formData.longitude}
              onChange={handleCoordinateChange}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.longitude ? 'border-red-500' : 'border-gray-300'
                }`}
              placeholder="e.g., -74.0060"
            />
            {formData.longitude && !coordinatesManuallyChanged.longitude && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                  Auto-filled
                </span>
              </div>
            )}
          </div>
          {errors.longitude && (
            <p className="text-sm text-red-600">{errors.longitude}</p>
          )}
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <XCircle className="w-4 h-4 mr-2" />
          Cancel
        </button>
        <button
          type="submit"
          className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Save className="w-4 h-4 mr-2" />
          {firm ? 'Update Firm' : 'Save Firm'}
        </button>
      </div>
    </form>
  );
};

export default FirmForm;